import { toast } from 'react-toastify';
import { Schema, Node as ProseMirrorNode } from '@tiptap/pm/model';

import { getTranslateByKey } from '@/main/services/utils';
import { TemplateEmailNotificationVariableResponse } from '@/main/models/response/templateEmailNotificationResponse';

import { TEMPLATE_VARIABLE_REGEX } from './extensions/templateVariable';

export type VariablePasteResult =
  | { status: 'passthrough' } 
  | { status: 'invalid' } 
  | { status: 'valid' }; 

export function validateVariablePaste(
  text: string,
  availableVariables: TemplateEmailNotificationVariableResponse[],
  isSubject: boolean,
  cultureName?: string
): VariablePasteResult {
  if (!text) return { status: 'passthrough' };

  const prefix = cultureName ? `${cultureName} - ` : '';
  const hasBraces = text.includes('{') || text.includes('}');

  if (hasBraces && !text.includes('{{')) {
    toast.error(`${prefix}${getTranslateByKey('information.variableNotFound')}`);
    return { status: 'invalid' };
  }

  if (!hasBraces) return { status: 'passthrough' };

  const matches = [...text.matchAll(new RegExp(TEMPLATE_VARIABLE_REGEX.source, 'g'))];
  if (matches.length === 0) {
    toast.error(`${prefix}${getTranslateByKey('information.variableNotFound')}`);
    return { status: 'invalid' };
  }

  const stripped = text.replace(new RegExp(TEMPLATE_VARIABLE_REGEX.source, 'g'), '');
  if (stripped.includes('{') || stripped.includes('}')) {
    toast.error(`${prefix}${getTranslateByKey('information.variableNotFound')}`);
    return { status: 'invalid' };
  }

  const availableNames = new Set(
    availableVariables
      .filter(v => v.isSubject === isSubject)
      .map(v => v.variable.replace(/^\{\{|\}\}$/g, '').trim())
  );

  const invalid: string[] = [];
  for (const m of matches) {
    const name = m[1].trim();
    if (!availableNames.has(name)) invalid.push(m[0]);
  }

  if (invalid.length > 0) {
    const message = getTranslateByKey('information.variableNotFound');
    [...new Set(invalid)].forEach(v => toast.error(`${prefix}${v} - ${message}`));
    return { status: 'invalid' };
  }

  return { status: 'valid' };
}

export function buildVariableLineNodes(line: string, schema: Schema): ProseMirrorNode[] {
  const variableNodeType = schema.nodes.templateVariable;
  const nodes: ProseMirrorNode[] = [];
  let lastIndex = 0;

  const lineMatches = [...line.matchAll(new RegExp(TEMPLATE_VARIABLE_REGEX.source, 'g'))];
  for (const m of lineMatches) {
    const start = m.index ?? 0;
    if (start > lastIndex) {
      nodes.push(schema.text(line.slice(lastIndex, start)));
    }
    nodes.push(variableNodeType.create({ name: m[1].trim() }));
    lastIndex = start + m[0].length;
  }
  if (lastIndex < line.length) {
    nodes.push(schema.text(line.slice(lastIndex)));
  }

  return nodes;
}
